import { forwardRef, useMemo, useState } from 'react';
import {
  Box,
  Group,
  ActionIcon,
  Tooltip,
  Text,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import {
  IconTrash,
  IconGripVertical,
  IconSettings,
  IconEdit,
  IconCheck,
  IconBoxMultiple,
} from '@tabler/icons-react';
import { getContainer } from '../containerRegistry';
import { useDashboardStore } from '../../../../stores/dashboardStore';
import { ContainerInstance, DashboardGlobalData, ContainerSlot } from '../types';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { ChildWidgetRenderer } from './ChildWidgetRenderer';
import { EmptySlot } from './EmptySlot';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// ContainerWrapper Component
// ============================================================================

export interface ContainerWrapperProps {
  /** Container instance data */
  instance: ContainerInstance;
  /** Global dashboard data */
  globalData?: DashboardGlobalData;
  /** Whether edit mode is active */
  isEditMode?: boolean;
  /** Whether this container is being dragged */
  isDragging?: boolean;
  /** Drag start handler */
  onDragStart?: (e: React.DragEvent) => void;
  /** Drag end handler */
  onDragEnd?: () => void;
  /** Drag over handler */
  onDragOver?: (e: React.DragEvent) => void;
  /** Drop handler */
  onDrop?: (e: React.DragEvent) => void;
}

export const ContainerWrapper = forwardRef<HTMLDivElement, ContainerWrapperProps>(
  (
    {
      instance,
      globalData,
      isEditMode = false,
      isDragging = false,
      onDragStart,
      onDragEnd,
      onDragOver,
      onDrop,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const container = getContainer(instance.containerId);
    const { isMobile } = useBreakpoint();
    const [isHovered, setIsHovered] = useState(false);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Store actions
    const removeContainer = useDashboardStore((state) => state.removeContainer);
    const editingContainerId = useDashboardStore((state) => state.editingContainerId);
    const setEditingContainer = useDashboardStore((state) => state.setEditingContainer);
    const setContainerSettings = useDashboardStore((state) => state.setContainerSettings);
    const draggedWidgetId = useDashboardStore((state) => state.draggedWidgetId);

    // Check if this container is being edited
    const isEditingThisContainer = editingContainerId === instance.instanceId;

    // Check if a widget is being dragged (not a container)
    const isWidgetBeingDragged = !!draggedWidgetId;

    // Calculate internal grid layout based on breakpoint
    const internalLayout = useMemo(() => {
      if (!container) return { cols: 1, rows: 1 };

      // On mobile, stack children vertically
      if (isMobile) {
        const totalSlots = container.internalGrid.cols * container.internalGrid.rows;
        return {
          cols: 1,
          rows: totalSlots,
        };
      }

      return container.internalGrid;
    }, [container, isMobile]);

    // Get slot positions (adjusted for mobile)
    const getSlotPosition = (slot: ContainerSlot, index: number) => {
      if (isMobile) {
        return {
          col: 0,
          row: index,
          colSpan: 1,
          rowSpan: 1,
        };
      }
      return {
        col: slot.col,
        row: slot.row,
        colSpan: slot.colSpan || 1,
        rowSpan: slot.rowSpan || 1,
      };
    };

    // Handle edit container toggle
    const handleEditContainer = () => {
      if (isEditingThisContainer) {
        setEditingContainer(null);
      } else {
        setEditingContainer(instance.instanceId);
      }
    };

    // Handle remove container
    const handleRemoveContainer = () => {
      removeContainer(instance.instanceId);
    };

    // Check if container has any empty slots
    const hasEmptySlots = container
      ? container.slots.some((slot) => !instance.children.find((c) => c.slotId === slot.id))
      : false;

    // Custom drag handlers that don't intercept when a widget is being dragged
    const handleContainerDragOver = (e: React.DragEvent) => {
      // If a widget is being dragged and this container has empty slots, let it bubble to EmptySlot
      if (isWidgetBeingDragged && hasEmptySlots) {
        return; // Don't prevent default, let it bubble
      }
      // Otherwise, handle container reordering
      onDragOver?.(e);
    };

    const handleContainerDrop = (e: React.DragEvent) => {
      // If a widget is being dragged, let it bubble to EmptySlot
      if (isWidgetBeingDragged) {
        return; // Don't handle, let it bubble
      }
      // Otherwise, handle container reordering
      onDrop?.(e);
    };

    // Count filled and empty slots
    const filledSlots = instance.children.length;
    const totalSlots = container?.slots.length || 0;

    if (!container) {
      return (
        <Box
          ref={ref}
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.colors.gray[5],
          }}
        >
          Container not found
        </Box>
      );
    }

    return (
      <Box
        ref={ref}
        style={{
          height: '100%',
          position: 'relative',
          opacity: isDragging ? 0.5 : 1,
          transition: 'all 150ms ease',
          // Show container border in edit mode
          ...(isEditMode && {
            border: isEditingThisContainer
              ? `2px solid ${theme.colors.blue[5]}`
              : `2px dashed ${
                  colorScheme === 'dark' ? theme.colors.violet[7] : theme.colors.violet[3]
                }`,
            borderRadius: theme.radius.md,
            backgroundColor:
              colorScheme === 'dark'
                ? 'rgba(139, 92, 246, 0.05)'
                : 'rgba(139, 92, 246, 0.03)',
          }),
        }}
        draggable={isEditMode && !isEditingThisContainer}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragOver={handleContainerDragOver}
        onDrop={handleContainerDrop}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Container label in edit mode */}
        {isEditMode && (
          <Box
            style={{
              position: 'absolute',
              top: -10,
              left: 8,
              zIndex: 50,
              backgroundColor:
                colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
              padding: '2px 8px',
              borderRadius: theme.radius.sm,
              border: `1px solid ${
                isEditingThisContainer
                  ? theme.colors.blue[5]
                  : colorScheme === 'dark'
                    ? theme.colors.violet[7]
                    : theme.colors.violet[3]
              }`,
            }}
          >
            <Group gap={4}>
              <IconBoxMultiple
                size={12}
                color={
                  isEditingThisContainer
                    ? theme.colors.blue[5]
                    : theme.colors.violet[5]
                }
              />
              <Text
                size="xs"
                fw={500}
                c={isEditingThisContainer ? 'blue' : 'violet'}
                style={accessibilityStyles}
              >
                {container.name}
              </Text>
              <Text size="xs" c="dimmed" style={accessibilityStyles}>
                ({filledSlots}/{totalSlots})
              </Text>
            </Group>
          </Box>
        )}

        {/* Edit mode controls overlay */}
        {isEditMode && (isHovered || isEditingThisContainer) && (
          <Box
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              zIndex: 100,
              display: 'flex',
              gap: 4,
              backgroundColor:
                colorScheme === 'dark'
                  ? 'rgba(0, 0, 0, 0.8)'
                  : 'rgba(255, 255, 255, 0.95)',
              borderRadius: theme.radius.sm,
              padding: 4,
              boxShadow: theme.shadows.sm,
            }}
          >
            {/* Drag handle */}
            {!isEditingThisContainer && (
              <Tooltip label="Drag to reorder" withArrow position="top">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="gray"
                  style={{ cursor: 'grab' }}
                >
                  <IconGripVertical size={14} />
                </ActionIcon>
              </Tooltip>
            )}

            {/* Edit container button */}
            <Tooltip
              label={isEditingThisContainer ? 'Done editing' : 'Edit container contents'}
              withArrow
              position="top"
            >
              <ActionIcon
                size="sm"
                variant={isEditingThisContainer ? 'filled' : 'subtle'}
                color={isEditingThisContainer ? 'blue' : 'gray'}
                onClick={handleEditContainer}
              >
                {isEditingThisContainer ? <IconCheck size={14} /> : <IconEdit size={14} />}
              </ActionIcon>
            </Tooltip>

            {/* Remove container */}
            <Tooltip label="Remove container" withArrow position="top">
              <ActionIcon
                size="sm"
                variant="subtle"
                color="red"
                onClick={handleRemoveContainer}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Tooltip>
          </Box>
        )}

        {/* Container internal grid */}
        <Box
          style={{
            height: '100%',
            display: 'grid',
            gridTemplateColumns: `repeat(${internalLayout.cols}, 1fr)`,
            gridTemplateRows: `repeat(${internalLayout.rows}, 1fr)`,
            gap: theme.spacing.xs,
            padding: isEditMode ? theme.spacing.xs : 0,
            paddingTop: isEditMode ? theme.spacing.md : 0,
          }}
        >
          {container.slots.map((slot, index) => {
            const child = instance.children.find((c) => c.slotId === slot.id);
            const position = getSlotPosition(slot, index);

            return (
              <Box
                key={slot.id}
                style={{
                  gridColumn: `${position.col + 1} / span ${position.colSpan}`,
                  gridRow: `${position.row + 1} / span ${position.rowSpan}`,
                  minHeight: 0,
                  overflow: 'hidden',
                }}
              >
                {child ? (
                  <ChildWidgetRenderer
                    containerInstanceId={instance.instanceId}
                    child={child}
                    globalData={globalData}
                    isEditing={isEditingThisContainer}
                    isGlobalEditMode={isEditMode}
                  />
                ) : (isEditMode || isEditingThisContainer) ? (
                  <EmptySlot
                    containerInstanceId={instance.instanceId}
                    slotId={slot.id}
                    isCompact={!isEditingThisContainer && isEditMode}
                  />
                ) : null}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }
);

ContainerWrapper.displayName = 'ContainerWrapper';

export default ContainerWrapper;
